/**
 * The field the panel has been telling operators to paste into.
 *
 * "Paste the public key into the field above" has been step four of the first
 * procedure since the panel was written, and there was no field — so the first
 * step of the first procedure could not be completed and every instruction
 * after it was unreachable. The offline tool worked, the recovery flow worked,
 * and there was no way to connect the two.
 *
 * **Only the public half arrives here**, which is the whole design. The private
 * key was split into shares on somebody's own machine and this server has never
 * seen it and never will. Registering a key enables *sealing* group keys to it;
 * opening them again needs the shares, gathered deliberately, in a room.
 *
 * The share count and threshold are typed in rather than read from the key,
 * because they are not in it — they describe how the private half was split,
 * which happened somewhere this server cannot see. They are recorded so the
 * panel can later tell an operator what they are looking for.
 */

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { errorMessage, trpc, trpcClient } from "../../lib/trpc.js";
import { Button } from "../../components/ui/Button.js";
import { Field } from "../../components/ui/Field.js";
import { Input } from "../../components/ui/Input.js";
import { useToast } from "../../components/ui/Toast.js";
import "./RegisterRecoveryKey.css";

export function RegisterRecoveryKey({ replacing }: { replacing: boolean }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const [publicKey, setPublicKey] = useState("");
  const [label, setLabel] = useState("");
  const [shareCount, setShareCount] = useState("3");
  const [threshold, setThreshold] = useState("2");
  const [error, setError] = useState<string | null>(null);

  const register = useMutation({
    mutationFn: () =>
      trpcClient.admin.registerRecoveryKey.mutate({
        publicKey: publicKey.trim(),
        shareCount: Number(shareCount),
        threshold: Number(threshold),
        ...(label.trim() ? { label: label.trim() } : {}),
      }),
    onSuccess: async () => {
      setError(null);
      setPublicKey("");
      setLabel("");
      setOpen(false);
      toast("Recovery key recorded. Verify it with two shares next.");
      await queryClient.invalidateQueries({
        queryKey: trpc.admin.securityStatus.queryKey(),
      });
    },
    onError: (caught) => setError(errorMessage(caught)),
  });

  const shares = Number(shareCount);
  const needed = Number(threshold);
  const numbersOk =
    Number.isInteger(shares) &&
    Number.isInteger(needed) &&
    shares >= 2 &&
    needed >= 2 &&
    needed <= shares;

  if (!open) {
    return (
      <div className="fc-regkey__open">
        <Button variant="secondary" onClick={() => setOpen(true)}>
          {replacing ? "Register a new key" : "Paste the public key"}
        </Button>
      </div>
    );
  }

  return (
    <form
      className="fc-regkey"
      onSubmit={(event) => {
        event.preventDefault();
        register.mutate();
      }}
    >
      {error && (
        <p className="fc-admin__error" role="alert">
          {error}
        </p>
      )}

      {/*
        Said before the paste, not after. Somebody who has just run a tool that
        printed several things needs to know which one goes here, and the wrong
        one is a private key.
      */}
      <p className="fc-regkey__note">
        The <strong>public</strong> key the tool printed — the line beginning{" "}
        <code>MII</code>. Never a share file, and never the passphrase.
      </p>

      <Field label="Public key" htmlFor="fc-regkey-public" required>
        <textarea
          id="fc-regkey-public"
          className="fc-regkey__key"
          rows={4}
          spellCheck={false}
          autoComplete="off"
          value={publicKey}
          placeholder="MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8A…"
          onChange={(event) => setPublicKey(event.target.value)}
        />
      </Field>

      <div className="fc-regkey__row">
        <Field
          label="Shares"
          htmlFor="fc-regkey-shares"
          hint="How many pieces the private key was split into."
        >
          <Input
            id="fc-regkey-shares"
            type="number"
            min={2}
            max={16}
            value={shareCount}
            onChange={(event) => setShareCount(event.target.value)}
          />
        </Field>

        <Field
          label="Needed to rebuild"
          htmlFor="fc-regkey-threshold"
          hint="Any fewer reveals nothing at all."
        >
          <Input
            id="fc-regkey-threshold"
            type="number"
            min={2}
            max={16}
            value={threshold}
            onChange={(event) => setThreshold(event.target.value)}
          />
        </Field>
      </div>

      <Field
        label="Label"
        htmlFor="fc-regkey-label"
        hint="Optional. Where the shares went, so generations can be told apart."
      >
        <Input
          id="fc-regkey-label"
          maxLength={120}
          value={label}
          placeholder="safe, office, lawyer"
          onChange={(event) => setLabel(event.target.value)}
        />
      </Field>

      {replacing && (
        <p className="fc-regkey__note fc-regkey__note--warn">
          {/*
            Retiring rather than replacing: groups already sealed to the current
            key stay recoverable with the shares that open it. Overwriting the
            record would leave them sealed to a key with no note of which shares
            it belongs to.
          */}
          This retires the current key. Groups already sealed to it stay
          recoverable with its shares — keep them.
        </p>
      )}

      <div className="fc-regkey__actions">
        <Button
          type="submit"
          variant="primary"
          loading={register.isPending}
          disabled={publicKey.trim().length < 64 || !numbersOk}
        >
          Record this key
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            setOpen(false);
            setError(null);
          }}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}
